import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { lockBodyScroll, unlockBodyScroll } from "../utils/scrollLock";

type SupportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const SupportModal: React.FC<SupportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    lockBodyScroll();

    const prevActive = document.activeElement as HTMLElement | null;

    const id = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
      setIsVisible(true);
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", onKeyDown);
      if (prevActive) {
        prevActive.focus();
      }
      window.cancelAnimationFrame(id);
      setIsVisible(false);
      setIsExiting(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const closeWithAnimation = (cb: () => void) => {
    setIsExiting(true);
    setTimeout(() => {
      cb();
    }, 300);
  };

  const handleClose = () => closeWithAnimation(onClose);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Modal Card */}
      <div
        style={{
          position: "relative",
          width: "min(420px, 92vw)",
          maxWidth: "420px",
          background: "var(--modal-bg)",
          color: "rgba(255, 255, 255, 0.9)",
          borderRadius: "14px",
          padding: "24px 24px 20px",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          transform: isExiting
            ? "translateY(40px)"
            : isVisible
              ? "translateY(0)"
              : "translateY(40px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <h2
            id="support-modal-title"
            style={{
              margin: 0,
              fontSize: "22px",
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.95)",
            }}
          >
            Thank you for using Polytrome
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            style={{
              padding: "8px",
              borderRadius: "8px",
              border: "none",
              background: "rgba(156, 163, 175, 0.2)",
              color: "rgba(255, 255, 255, 0.7)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(156, 163, 175, 0.3)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 0.9)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(156, 163, 175, 0.2)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)";
            }}
            aria-label="Close support reminder"
          >
            {FaTimes({})}
          </button>
        </div>

        {/* Body */}
        <p
          style={{
            margin: 0,
            fontSize: "15px",
            lineHeight: 1.6,
            color: "rgba(255, 255, 255, 0.85)",
          }}
        >
          If you found Polytrome useful and would like to keep it free, you can
          support its development and maintenance by donating. Thank you! -
          Pavle
        </p>

        {/* Ko-fi button (matching About page) */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "8px",
          }}
        >
          <a
            href="https://ko-fi.com/polytrome"
            target="_blank"
            rel="noopener noreferrer"
            className="back-button kofi-button"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              // borderRadius: "999px",
              textDecoration: "none",
              fontSize: "14px",
            }}
          >
            <img
              src="/kofiLogo.webp"
              alt="Ko-fi"
              className="kofi-logo"
              style={{ width: 20, height: 20, objectFit: "contain" }}
            />
            <span>Support on Ko-fi</span>
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
};
